import React, { memo, useCallback } from "react";

import { Box, Text, HStack, VStack, Spacer } from "@chakra-ui/react";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import { useAuth } from "modules/providers/auth-provider";
import FileThumb from "modules/files/file-thumb";
import colors from "app/colors";
import { TrashOutlineIcon, DownloadIcon } from "modules/icons";
import { FileGalleryFragment, MessageThreadFragment } from "gql/graphql";
import useDownloadFile from "modules/hooks/use-download-file";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import UserName from "../../../_shared/user-name";
import moment from "moment";
import { MESSAGE_CALENDAR_FORMATS } from "modules/utils/Constants";
import MessageFileDrawerReactionsPopover from "./message-file-drawer-reactions-popover";
import MessageLink from "./message-link";
import useRemovedFromThread from "modules/hooks/use-removed-from-thread";
import MessageFileReactions from "modules/messaging/messages/message-file-reactions";

interface IProps {
  messageThread?: MessageThreadFragment | null;
  file: FileGalleryFragment;
}

const MessageThreadFilesDrawerFile = ({ messageThread, file }: IProps) => {
  const { me } = useAuth();
  const { downloadFile } = useDownloadFile(file);
  const { openModal } = useOverlay();
  const { isMeRemoved } = useRemovedFromThread(messageThread);

  const onDeleteFileClicked = useCallback(
    (fileId: string) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      if (!messageThread) return;
      openModal({
        type: MODAL_TYPE.DELETE_FILE,
        id: fileId,
        id2: messageThread.id,
      });
    },
    [messageThread, openModal],
  );

  const openGallery = useCallback(
    (fileId: string) => {
      if (!messageThread) return;
      openModal({
        type: MODAL_TYPE.MESSAGE_THREAD_FILE_GALLERY,
        id: messageThread.id,
        id2: fileId,
      });
    },
    [messageThread, openModal],
  );

  if (!me) return null;

  return (
    <Box
      h={118}
      alignContent="center"
      w="full"
      borderBottomWidth={1}
      borderBottomColor={colors.gray0}
    >
      <HStack spacing={4} h={86} w="full">
        <Box
          cursor="pointer"
          onClick={() => {
            openGallery(file.id);
          }}
        >
          <FileThumb
            contentType={file.contentType}
            name={file.name}
            url={file.previewCloudFrontUrl}
          />
        </Box>
        <VStack alignItems="flex-start" spacing={0} w="full" h="full">
          <Text
            fontSize={16}
            fontWeight={700}
            noOfLines={1}
            color={colors.blackSecondary}
            wordBreak="break-all"
          >
            {file.name}
          </Text>
          <HStack
            color={colors.gray50}
            fontSize={14}
            fontWeight={400}
            spacing={1}
          >
            <Text>
              <UserName userId={file.userId} />
            </Text>
            <Text>•</Text>
            <Text>
              {moment(file.createTime).calendar(MESSAGE_CALENDAR_FORMATS)}
            </Text>
          </HStack>
          <MessageLink
            messageThreadId={file.messageThreadId}
            messageId={file.messageId}
            isVoiceNote={file.isVoiceNote}
          />
          {file.reactionList.length > 0 && (
            <MessageFileDrawerReactionsPopover file={file}>
              <MessageFileReactions
                reactions={file.reactionList}
                color={colors.gray50}
              />
            </MessageFileDrawerReactionsPopover>
          )}
        </VStack>
        <Spacer />
        <HStack spacing={4} alignItems="flex-start" h="full">
          {file.userId === me.id && !isMeRemoved && (
            <CircularIconButton
              minW={8}
              h={8}
              aria-label="delete"
              color={colors.gray40}
              backgroundColor={colors.gray40}
              isDisabled={me.id !== file.userId}
              icon={<TrashOutlineIcon boxSize={4} fill={colors.white} />}
              onClick={onDeleteFileClicked(file.id)}
            />
          )}
          <CircularIconButton
            minW={8}
            h={8}
            aria-label="download"
            color={colors.gray40}
            backgroundColor={colors.gray40}
            onClick={downloadFile}
            icon={<DownloadIcon boxSize={4} fill={colors.white} />}
          />
        </HStack>
      </HStack>
    </Box>
  );
};

export default memo(MessageThreadFilesDrawerFile);
